import { ref, reactive } from 'vue'
import type { Vehicle } from '../types/vehicle'
import type { Driver } from '../types/driver'
import type { ScheduleEntry } from '../types/schedule'

export type ConflictType = 'vehicle' | 'driver' | 'schedule'

export interface ConflictData<T = any> {
  id: string
  type: ConflictType
  localVersion: T
  remoteVersion: T
  lastModified: Date
  conflictedFields: string[]
  timestamp: Date
}

export interface ConflictResolution<T = any> {
  conflictId: string
  resolution: 'local' | 'remote' | 'merge'
  mergedData?: Partial<T>
}

export interface OptimisticLockData {
  version: number
  lastModified: Date
}

class ConflictResolutionService {
  private conflicts = reactive<Map<string, ConflictData>>(new Map())
  private optimisticLocks = reactive<Map<string, OptimisticLockData>>(new Map())
  private conflictCallbacks = new Set<(conflict: ConflictData) => void>()
  private resolutionCallbacks = new Set<(resolution: ConflictResolution) => void>()

  // Optimistic locking methods
  public setOptimisticLock(entityId: string, version: number, lastModified: Date): void {
    this.optimisticLocks.set(entityId, { version, lastModified })
  }

  public getOptimisticLock(entityId: string): OptimisticLockData | null {
    return this.optimisticLocks.get(entityId) || null
  }

  public removeOptimisticLock(entityId: string): void {
    this.optimisticLocks.delete(entityId)
  }

  public checkOptimisticLock(entityId: string, expectedVersion: number): boolean {
    const lock = this.optimisticLocks.get(entityId)
    return !lock || lock.version === expectedVersion
  }

  // Conflict detection and management
  public detectConflict<T>(
    entityId: string,
    type: ConflictType,
    localVersion: T,
    remoteVersion: T,
    conflictedFields: string[]
  ): ConflictData<T> {
    const conflict: ConflictData<T> = {
      id: `${type}-${entityId}-${Date.now()}`,
      type,
      localVersion,
      remoteVersion,
      lastModified: new Date(),
      conflictedFields,
      timestamp: new Date()
    }

    this.conflicts.set(conflict.id, conflict)
    this.notifyConflictDetected(conflict)
    
    return conflict
  }

  public getConflict(conflictId: string): ConflictData | null {
    return this.conflicts.get(conflictId) || null
  }

  public getAllConflicts(): ConflictData[] {
    return Array.from(this.conflicts.values())
  }

  public getConflictsByType(type: ConflictType): ConflictData[] {
    return Array.from(this.conflicts.values()).filter(conflict => conflict.type === type)
  }

  public resolveConflict<T>(resolution: ConflictResolution<T>): void {
    const conflict = this.conflicts.get(resolution.conflictId)
    if (!conflict) {
      throw new Error(`Conflict with ID ${resolution.conflictId} not found`)
    }

    // Remove the conflict from the active conflicts
    this.conflicts.delete(resolution.conflictId)
    
    // Notify resolution callbacks
    this.notifyConflictResolved(resolution)
  }

  public dismissConflict(conflictId: string): void {
    this.conflicts.delete(conflictId)
  }

  // Merge strategies
  public createMergedData<T extends Record<string, any>>(
    localVersion: T,
    remoteVersion: T,
    fieldResolutions: Record<string, 'local' | 'remote'>
  ): T {
    const merged = { ...localVersion } as T

    Object.entries(fieldResolutions).forEach(([field, resolution]) => {
      if (resolution === 'remote' && field in remoteVersion) {
        (merged as any)[field] = remoteVersion[field]
      }
      // 'local' resolution keeps the existing value from localVersion
    })

    return merged
  }

  // Auto-refresh functionality
  public shouldAutoRefresh(entityType: ConflictType, entityId: string): boolean {
    // Check if there are any active conflicts for this entity
    const entityConflicts = Array.from(this.conflicts.values()).filter(conflict => 
      conflict.type === entityType && 
      (conflict.localVersion as any)?.id === entityId
    )
    
    return entityConflicts.length === 0 // Auto-refresh only if no conflicts
  }

  public triggerAutoRefresh(entityType: ConflictType, entityId: string): void {
    if (this.shouldAutoRefresh(entityType, entityId)) {
      // Emit event for auto-refresh
      window.dispatchEvent(new CustomEvent('auto-refresh', {
        detail: { entityType, entityId }
      }))
    }
  }

  // Notification system
  public onConflictDetected(callback: (conflict: ConflictData) => void): () => void {
    this.conflictCallbacks.add(callback)
    return () => this.conflictCallbacks.delete(callback)
  }

  public onConflictResolved(callback: (resolution: ConflictResolution) => void): () => void {
    this.resolutionCallbacks.add(callback)
    return () => this.resolutionCallbacks.delete(callback)
  }

  private notifyConflictDetected(conflict: ConflictData): void {
    this.conflictCallbacks.forEach(callback => {
      try {
        callback(conflict)
      } catch (error) {
        console.error('Error in conflict detection callback:', error)
      }
    })
  }

  private notifyConflictResolved(resolution: ConflictResolution): void {
    this.resolutionCallbacks.forEach(callback => {
      try {
        callback(resolution)
      } catch (error) {
        console.error('Error in conflict resolution callback:', error)
      }
    })
  }

  // Utility methods
  public getConflictedFieldsForVehicle(local: Vehicle, remote: Vehicle): string[] {
    const fields: string[] = []
    
    if (local.registrationNumber !== remote.registrationNumber) fields.push('registrationNumber')
    if (local.vehicleType !== remote.vehicleType) fields.push('vehicleType')
    if (local.brandModel !== remote.brandModel) fields.push('brandModel')
    if (local.seatingCapacity !== remote.seatingCapacity) fields.push('seatingCapacity')
    if (local.status !== remote.status) fields.push('status')
    
    return fields
  }

  public getConflictedFieldsForDriver(local: Driver, remote: Driver): string[] {
    const fields: string[] = []
    
    if (local.fullName !== remote.fullName) fields.push('fullName')
    if (local.phoneNumber !== remote.phoneNumber) fields.push('phoneNumber')
    if (local.status !== remote.status) fields.push('status')
    
    return fields
  }

  public getConflictedFieldsForSchedule(local: ScheduleEntry, remote: ScheduleEntry): string[] {
    const fields: string[] = []
    
    if (local.usageDate.getTime() !== remote.usageDate.getTime()) fields.push('usageDate')
    if (local.startTime !== remote.startTime) fields.push('startTime')
    if (local.endTime !== remote.endTime) fields.push('endTime')
    if (local.vehicleId !== remote.vehicleId) fields.push('vehicleId')
    if (local.driverId !== remote.driverId) fields.push('driverId')
    if (local.notes !== remote.notes) fields.push('notes')
    
    return fields
  }

  // Cleanup
  public clearAllConflicts(): void {
    this.conflicts.clear()
    this.optimisticLocks.clear()
  }

  public clearExpiredConflicts(maxAge: number = 30 * 60 * 1000): void { // 30 minutes default
    const now = new Date()
    const expiredConflicts: string[] = []

    this.conflicts.forEach((conflict, id) => {
      if (now.getTime() - conflict.timestamp.getTime() > maxAge) {
        expiredConflicts.push(id)
      }
    })

    expiredConflicts.forEach(id => this.conflicts.delete(id))
  }
}

export const conflictResolutionService = new ConflictResolutionService()